import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isUserAdmin } from "../Utils/authHelpers";
import "../css/AdminFloatingButton.css";

/**
 * Universal Draggable Floating Action Button (FAB) for Aminii Admin users.
 * - Circular round button with shield icon only (no text label).
 * - Smoothly draggable anywhere across the viewport.
 * - Distinguishes dragging from clicking (clicking navigates to /AdminAminii).
 * - Automatically hidden on the /AdminAminii page itself.
 */
function AdminFloatingButton() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem("aminii_admin_fab_pos");
      if (saved) return JSON.parse(saved);
    } catch {}
    // Default: bottom right with nice margin
    const w = typeof window !== "undefined" ? window.innerWidth : 1200;
    const h = typeof window !== "undefined" ? window.innerHeight : 800;
    return { x: Math.max(10, w - 70), y: Math.max(10, h - 85) };
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, posX: 0, posY: 0, moved: false });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkRole = () => {
      try {
        setIsAdmin(Boolean(isUserAdmin()));
      } catch {
        setIsAdmin(false);
      }
    };

    checkRole();
    window.addEventListener("storage", checkRole);
    window.addEventListener("authChange", checkRole);

    return () => {
      window.removeEventListener("storage", checkRole);
      window.removeEventListener("authChange", checkRole);
    };
  }, [location.pathname]);

  // Keep position clamped within screen on window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => ({
        x: Math.min(Math.max(10, prev.x), window.innerWidth - 60),
        y: Math.min(Math.max(10, prev.y), window.innerHeight - 60),
      }));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePointerDown = (e) => {
    // Only accept left click or touch
    if (e.button !== undefined && e.button !== 0) return;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX === undefined) return;

    dragRef.current = {
      startX: clientX,
      startY: clientY,
      posX: position.x,
      posY: position.y,
      moved: false,
    };
    setIsDragging(true);

    const onPointerMove = (moveEvent) => {
      const curX = moveEvent.clientX ?? moveEvent.touches?.[0]?.clientX;
      const curY = moveEvent.clientY ?? moveEvent.touches?.[0]?.clientY;
      if (curX === undefined) return;

      const deltaX = curX - dragRef.current.startX;
      const deltaY = curY - dragRef.current.startY;

      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
        dragRef.current.moved = true;
      }

      const nextX = Math.min(Math.max(10, dragRef.current.posX + deltaX), window.innerWidth - 60);
      const nextY = Math.min(Math.max(10, dragRef.current.posY + deltaY), window.innerHeight - 60);

      setPosition({ x: nextX, y: nextY });
    };

    const onPointerUp = () => {
      setIsDragging(false);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerUp);

      setPosition((curr) => {
        try {
          localStorage.setItem("aminii_admin_fab_pos", JSON.stringify(curr));
        } catch {}
        return curr;
      });
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("touchmove", onPointerMove);
    window.addEventListener("touchend", onPointerUp);
  };

  const handleClick = (e) => {
    if (dragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    navigate("/AdminAminii");
  };

  if (!isAdmin || location.pathname.toLowerCase().startsWith("/adminaminii")) {
    return null;
  }

  return (
    <button
      className={`aminii-global-admin-fab${isDragging ? " is-dragging" : ""}`}
      style={{ left: `${position.x}px`, top: `${position.y}px`, right: "auto", bottom: "auto" }}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      aria-label="Open Admin Dashboard"
      title="Admin Dashboard (Drag anywhere)"
      type="button"
    >
      <div className="aminii-admin-fab-glow" />
      <div className="aminii-admin-fab-content">
        <svg
          className="aminii-admin-fab-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      </div>
    </button>
  );
}

export default AdminFloatingButton;
